/*!
VueLayers
Web map Vue components with the power of OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.10.25
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var olTilecache = require('ol-tilecache');
var consts = require('../ol-ext/consts');
var extent = require('../ol-ext/extent');
var tileGrid = require('../ol-ext/tile-grid');
var assert = require('../util/assert');
var minilo = require('../util/minilo');
var source = _interopDefault(require('./source'));
var withUrl = _interopDefault(require('./with-url'));

var props = {
  cacheSize: {
    type: Number,
    default: consts.CACHE_SIZE
  },
  crossOrigin: String,
  maxZoom: {
    type: Number,
    default: consts.MAX_ZOOM
  },
  minZoom: {
    type: Number,
    default: consts.MIN_ZOOM
  },
  opaque: Boolean,
  projection: {
    type: String,
    default: consts.EPSG_3857
  },
  reprojectionErrorThreshold: {
    type: Number,
    default: consts.REPROJ_ERR_THRESHOLD
  },
  tilePixelRatio: {
    type: Number,
    default: consts.PIXEL_RATIO
  },
  tileSize: {
    type: Array,
    default: function _default() {
      return [consts.TILE_SIZE, consts.TILE_SIZE];
    },
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  tileLoadFunction: Function,
  url: {
    type: [String, Function],
    required: true
  },
  /**
   * Duration of the opacity transition for rendering. To disable the opacity transition, pass `0`.
   * @type {number}
   */
  transition: Number
};

var computed = {
  /**
   * @type {string}
   */
  urlTmpl: function urlTmpl() {
    if (!minilo.isString(this.url)) {
      return '';
    }
    return minilo.replaceTokens(this.url, minilo.pick(this, this.urlTokens));
  }
};

var methods = {
  /**
   * @return {ol.tilegrid.TileGrid}
   * @protected
   */
  createTileGrid: function createTileGrid() {
    assert.hasView(this);

    return tileGrid.createXyzGrid({
      extent: extent.createExtentFromProjection(this.$view.getProjection()),
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      tileSize: this.tileSize
    });
  },

  /**
   * @return {ol.TileUrlFunction}
   * @protected
   */
  createUrlFunc: function createUrlFunc() {
    // custom url function provided
    if (minilo.isFunction(this.url)) {
      return this.url;
    }
    assert.hasView(this);
    // or use url function from ol-tilecache
    return olTilecache.createTileUrlFunction(this.urlTmpl, this._tileGrid, extent.createExtentFromProjection(this.$view.getProjection()));
  },

  /**
   * @return {Promise}
   * @protected
   */
  init: function init() {
    /**
     * @type {ol.tilegrid.TileGrid}
     * @protected
     */
    this._tileGrid = this.createTileGrid();

    return source.methods.init.call(this);
  },

  /**
   * @return {void|Promise<void>}
   * @protected
   */
  deinit: function deinit() {
    this._tileGrid = undefined;

    return source.methods.deinit.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    source.methods.mount.call(this);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    source.methods.mount.call(this);
  }
};

var watch = {
  url: function url() {
    if (this.$source) {
      this.$source.setTileUrlFunction(this.createUrlFunc());
      this.scheduleRefresh();
    }
  }
};

var tileSource = {
  mixins: [source, withUrl],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch
};

module.exports = tileSource;



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/mixin/tile-source.js
// module id = 126
// module chunks = 1